import {
    useEffect,
    useState,
    useRef,
    useMemo,
    useCallback,
    MutableRefObject,
    ReactNode
} from 'react'
import { Box, Typography, alpha, Button, useTheme } from '@mui/material'
import { ClassNames } from '@emotion/react'
import { StreamPlayerApi } from '@thriveglobal/cloudflare-stream-react'
import VideoPlayerThumbnail from './VideoPlayerThumbnail'
import { MEDIA_SOFT_COMPLETE_PERCENTAGE } from '../../../utils'
import { useIntl, defineMessages } from 'react-intl'
import FullscreenVideoPlayerPopup from '../StreamPlayer/FullscreenVideoPlayerPopup'
import { LeafIcon } from '@thriveglobal/thrive-web-leafkit'
import StreamPlayerWithHLSFallback from './StreamPlayerWithHLSFallback'

const intlMessages = defineMessages({
    closePlayer: {
        defaultMessage: 'Close video player',
        description: 'Close the video player'
    }
})

type Props = {
    title?: string
    thumbnail?: string
    closedCaptionLang?: string
    completePercentage?: number
    aspectRatio?: { width: number; height: number }
    overlayContent?: ReactNode
    onClose?: () => void
    fullscreen?: boolean
} & UseVideoPlayerOptions

export default function VideoPlayer({
    thumbnail,
    showLoading,
    src = '',
    title = '',
    autoplay,
    onEvent,
    closedCaptionLang = '',
    startTimeInSeconds = 0,
    timeUpdateDelayInSeconds = 10,
    completePercentage = MEDIA_SOFT_COMPLETE_PERCENTAGE,
    overlayContent,
    fullscreen = false,
    onClose
}: Props) {
    const {
        showTitleBanner,
        showThumbnail,
        streamRef,
        handleOnEvent,
        setShowThumbnail,
        playThumbnail,
        showOverlayContent,
        stopPlaying
    } = useVideoPlayer({
        src,
        autoplay,
        showLoading,
        onEvent,
        timeUpdateDelayInSeconds,
        startTimeInSeconds,
        overlayContent
    })

    const [isFullscreenPlayerOpen, setIsFullscreenPlayerOpen] = useState(false)
    const openFullscreenPlayer = useCallback(() => {
        setIsFullscreenPlayerOpen(true)
    }, [])
    const closeFullscreenPlayer = useCallback(() => {
        setIsFullscreenPlayerOpen(false)
    }, [])

    const handleOnClose = useCallback(() => {
        if (onClose) {
            stopPlaying()
            onClose()
        }
    }, [onClose, stopPlaying])

    const theme = useTheme()

    const intl = useIntl()

    const stream = useMemo(
        () => {
            return (
                <ClassNames>
                    {({ css, cx }) => {
                        const streamContainerClassname = fullscreen
                            ? cx(css`
                                  height: 100%;
                                  width: 100%;
                                  background-color: black;
                              `)
                            : cx(css`
                                  height: 100%;
                              `)

                        return (
                            <StreamPlayerWithHLSFallback
                                streamRef={streamRef}
                                src={src}
                                controls
                                autoplay
                                responsive={false}
                                width={'100%'}
                                height={'100%'}
                                className={streamContainerClassname}
                                defaultTextTrack={closedCaptionLang}
                                onLoadedMetaData={() =>
                                    handleOnEvent({ event: PLAYER_EVENT.ready })
                                }
                                onPlaying={() => {
                                    handleOnEvent({ event: PLAYER_EVENT.play })
                                }}
                                onPause={() => {
                                    handleOnEvent({ event: PLAYER_EVENT.pause })
                                }}
                                onEnded={() => {
                                    handleOnEvent({ event: PLAYER_EVENT.end })
                                    setShowThumbnail(true)
                                }}
                                onTimeUpdate={() => {
                                    const currentTime =
                                        streamRef.current?.currentTime || 0
                                    const totalTime =
                                        (streamRef.current as PlayerAPI)
                                            ?.duration || 0
                                    const progressPercentage = Math.round(
                                        (currentTime / totalTime) * 100
                                    )

                                    if (
                                        progressPercentage >= completePercentage
                                    ) {
                                        handleOnEvent({
                                            event: PLAYER_EVENT.complete
                                        })
                                    }
                                    handleOnEvent({ event: PLAYER_EVENT.time })
                                }}
                            />
                        )
                    }}
                </ClassNames>
            )
        },
        // Ignore only streamRef
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            fullscreen,
            closedCaptionLang,
            completePercentage,
            handleOnEvent,
            setShowThumbnail,
            src
        ]
    )

    return (
        <Box
            sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                position: 'relative',
                transition: 'height 1s',
                aspectRatio: '16 / 9',
                '&:hover .show-on-hover': {
                    opacity: 1
                }
            }}
        >
            <Box
                className="show-on-hover"
                sx={{
                    width: '100%',
                    position: 'absolute',
                    zIndex: 2,
                    // TO DO: this should be replaced with a scrim
                    background:
                        'linear-gradient(180deg, rgba(52, 62, 78, 0.84) 0%, rgba(52, 62, 78, 0.72) 49.48%, rgba(52, 62, 78, 0.3) 74.48%, rgba(52, 62, 78, 0) 100%);',
                    p: 2,
                    height: 70,
                    transition: 'opacity 2s',
                    opacity: showTitleBanner ? 1 : 0,
                    [theme.breakpoints.up('sm')]: {
                        p: 3,
                        height: 96
                    }
                }}
            >
                <Typography
                    variant="h5"
                    variantMapping={{ h5: 'h2' }}
                    color={theme.palette.primary.contrastText}
                >
                    {title}
                </Typography>
            </Box>

            {onClose && (
                <Button
                    variant="text"
                    className="show-on-hover"
                    sx={{
                        position: 'absolute',
                        zIndex: 2,
                        top: 10,
                        right: 10,
                        transition: 'opacity 2s',
                        opacity: showTitleBanner ? 1 : 0,
                        [theme.breakpoints.down('sm')]: {
                            opacity: 1
                        }
                    }}
                    onClick={handleOnClose}
                >
                    <LeafIcon
                        aria-label={intl.formatMessage(
                            intlMessages.closePlayer
                        )}
                        icon={'xmark'}
                        fontSize={'large'}
                        sx={{
                            color: theme.palette.common.white
                        }}
                    />
                </Button>
            )}

            {showOverlayContent && (
                <Box
                    sx={{
                        width: '100%',
                        position: 'absolute',
                        zIndex: 1,
                        color: 'white',
                        height: '100%',
                        bgcolor: alpha(theme.palette.grey[900], 0.6)
                    }}
                >
                    {overlayContent}
                </Box>
            )}

            {showLoading ||
                (showThumbnail && (
                    <VideoPlayerThumbnail
                        title={title}
                        thumbnail={thumbnail}
                        showLoading={showLoading}
                        playIconFontSize="72px"
                        onPlay={() => {
                            if (fullscreen) {
                                openFullscreenPlayer()
                            }
                            playThumbnail?.()
                        }}
                    />
                ))}

            {!showLoading && !showThumbnail && (
                <>
                    {fullscreen && (
                        <FullscreenVideoPlayerPopup
                            isOpen={isFullscreenPlayerOpen}
                            onClose={() => {
                                closeFullscreenPlayer()
                                setShowThumbnail(true)
                                handleOnClose()
                            }}
                        >
                            {stream}
                        </FullscreenVideoPlayerPopup>
                    )}
                    {!fullscreen && stream}
                </>
            )}
        </Box>
    )
}

export enum PLAYER_STATE {
    playing = 'playing',
    paused = 'paused',
    ended = 'ended',
    init = 'init',
    ready = 'ready'
}

export enum PLAYER_EVENT {
    play = 'play',
    pause = 'pause',
    end = 'end',
    complete = 'complete',
    time = 'time',
    ready = 'ready',
    change = 'change'
}

export interface PlayerEventArg {
    event: PLAYER_EVENT
    state: PLAYER_STATE
    player: PlayerAPI
    currentTimeInSeconds: number
    totalDurationInSeconds: number
}

export interface UseVideoPlayerAPI {
    showThumbnail: boolean
    playerState: PLAYER_STATE
    streamRef: MutableRefObject<StreamPlayerApi | undefined>
    showTitleBanner: boolean
    setShowThumbnail: (showThumbnail: boolean) => void
    handleOnEvent: (args: { event: PLAYER_EVENT }) => void
    playThumbnail?: () => void
    showOverlayContent: boolean
    stopPlaying: () => void
}

export interface UseVideoPlayerOptions {
    autoplay?: boolean
    showLoading?: boolean
    onEvent?: (args: PlayerEventArg) => void
    timeUpdateDelayInSeconds?: number
    startTimeInSeconds?: number
    src?: string
    overlayContent?: ReactNode
}

interface PlayerTimeRange {
    start: number
    end: number
}

export type PlayerAPI =
    | (StreamPlayerApi & {
          paused: boolean
          duration: number
          played: { ranges: PlayerTimeRange[] }
      })
    | undefined

function useVideoPlayer({
    src,
    autoplay,
    showLoading,
    onEvent,
    overlayContent,
    timeUpdateDelayInSeconds = 10,
    startTimeInSeconds = 0
}: UseVideoPlayerOptions): UseVideoPlayerAPI {
    const [showThumbnail, setShowThumbnail] = useState(!autoplay)
    const [playJustStartedTime, setPlayJustStartedTime] = useState(0)
    const [playJustStarted, setPlayJustStarted] = useState(false)
    const [timeUpdateWaiting, setTimeUpdateWaiting] = useState(true)
    const [playerState, setPlayerState] = useState<PLAYER_STATE>(
        PLAYER_STATE.init
    )

    const hasFiredCompletedEventRef = useRef(false)

    const streamRef = useRef<PlayerAPI>()

    const timeUpdateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
        null
    )

    const showTitleBanner =
        (playJustStarted ||
            playerState === PLAYER_STATE.paused ||
            playerState === PLAYER_STATE.init) &&
        !showLoading

    const showOverlayContent =
        playerState === PLAYER_STATE.ended && !!overlayContent

    const playThumbnail = useMemo(() => {
        if (showOverlayContent) return undefined

        return () => {
            setShowThumbnail(false)
        }
    }, [showOverlayContent])

    const stopPlaying = useCallback(() => {
        streamRef.current?.pause()
    }, [])

    const handleOnEvent = useCallback(
        (args: { event: PLAYER_EVENT }) => {
            let newPlayerState = playerState

            switch (args.event) {
                // video ended
                case PLAYER_EVENT.end: {
                    newPlayerState = PLAYER_STATE.ended
                    setPlayerState(newPlayerState)
                    setPlayJustStarted(false)
                    break
                }
                // The video has crossed the percentage set notify as complete (not that the video has ended)
                case PLAYER_EVENT.complete: {
                    if (!hasFiredCompletedEventRef.current) {
                        hasFiredCompletedEventRef.current = true
                        newPlayerState = PLAYER_STATE.playing
                        setPlayerState(newPlayerState)
                        break
                    } else {
                        // if we have already fired for the current video playing (src) ignore subsequent complete event
                        return
                    }
                }
                // video time progressed, every second while playing
                case PLAYER_EVENT.time: {
                    newPlayerState = PLAYER_STATE.playing
                    setPlayerState(newPlayerState)

                    // check if play has been on for a while
                    if (
                        (streamRef.current?.currentTime || 0) -
                            playJustStartedTime >=
                        5
                    ) {
                        setPlayJustStarted(false)
                    }

                    if (
                        timeUpdateWaiting &&
                        timeUpdateTimeoutRef.current !== null
                    ) {
                        // waiting and have a timeout in progress, do nothing else
                        return
                    }
                    if (timeUpdateWaiting) {
                        // waiting but do not have a timeout, start a timeout
                        timeUpdateTimeoutRef.current = setTimeout(() => {
                            setTimeUpdateWaiting(false)
                            timeUpdateTimeoutRef.current = null
                        }, timeUpdateDelayInSeconds * 1000)
                        // do nothing else
                        return
                    }
                    // start another waiting period and proceed to fire time event
                    setTimeUpdateWaiting(true)
                    break
                }
                // video loaded enough to start initial playing
                case PLAYER_EVENT.ready: {
                    newPlayerState = PLAYER_STATE.ready
                    setPlayerState(newPlayerState)

                    if (
                        streamRef.current &&
                        playerState !== PLAYER_STATE.ended
                    ) {
                        streamRef.current.currentTime = startTimeInSeconds
                    }
                    break
                }
                // video started playing
                case PLAYER_EVENT.play: {
                    newPlayerState = PLAYER_STATE.playing
                    setPlayerState(newPlayerState)
                    setPlayJustStarted(true)
                    setPlayJustStartedTime(streamRef.current?.currentTime || 0)
                    break
                }
                // video paused
                case PLAYER_EVENT.pause: {
                    newPlayerState = PLAYER_STATE.paused
                    setPlayerState(newPlayerState)
                    setPlayJustStarted(false)
                    break
                }
                // video src changed
                case PLAYER_EVENT.change: {
                    newPlayerState = PLAYER_STATE.init
                    setPlayerState(newPlayerState)

                    setPlayJustStarted(false)

                    // reset time update states once the video src changes
                    clearTimeout(
                        timeUpdateTimeoutRef.current as unknown as number
                    )
                    setTimeUpdateWaiting(true)
                    timeUpdateTimeoutRef.current = null
                    break
                }
            }

            if (onEvent) {
                onEvent({
                    event: args.event,
                    state: newPlayerState,
                    player: streamRef.current,
                    totalDurationInSeconds: streamRef.current?.duration || 0,
                    currentTimeInSeconds: streamRef.current?.currentTime || 0
                })
            }
        },
        [
            onEvent,
            playerState,
            timeUpdateDelayInSeconds,
            timeUpdateWaiting,
            startTimeInSeconds,
            playJustStartedTime
        ]
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (showOverlayContent) {
            setShowThumbnail(false)
        }

        hasFiredCompletedEventRef.current = false
        handleOnEvent({ event: PLAYER_EVENT.change })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src])

    useEffect(() => {
        setShowThumbnail(!autoplay)
    }, [src, autoplay])

    return {
        stopPlaying,
        playerState,
        showTitleBanner,
        showThumbnail,
        streamRef,
        handleOnEvent,
        setShowThumbnail,
        playThumbnail,
        showOverlayContent
    }
}
